import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import qs from 'qs';
import { proxiedPropertiesOf } from '@hooks/useForm/useForm.tsx';
import { first, isNotNullOrUndefined, isNullOrUndefined, isValidString, useLocalStorage } from '@utils/utils.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';

interface UseFilterProps<T> {
  onLoad: (page: FilterPage, filter: T) => void;
  filterName: string;
  defaultSort?: FilterSort;
  defaultFilter?: T;
  // Filter keys whose value is always an array (e.g. MultiSelect). A single value in the URL
  // (?statusEnums=ASSIGNING) parses to a string; these are coerced back to a one-element array
  // so multi-select controls render the selected chip.
  arrayFields?: string[];
  // Set false for unpaged views (e.g. the timeslot calendar) to keep pageNumber/pageSize out of the URL.
  pagingInUrl?: boolean;
  /**
   * Every filter key this list can set. It tells the hook which of its keys hold a single value, so a
   * hand-edited `?q=a&q=b` collapses instead of reaching code that expects a string.
   */
  filterKeys?: string[];
  /** Rows per page when neither the URL nor the stored state says otherwise. */
  defaultPageSize?: number;
}

interface ComponentProps {
  filters?: FilterElement[];
}

interface FilterElement {
  filterLabel: string;
  filterInput: any;
}

export interface FilterPage {
  pageNumber: number;
  pageSize: number;
}

export interface FilterSort {
  sortField: string;
  sortDirection: 'ASC' | 'DESC';
}

/** Written by every list, and unambiguously the list's own wherever they appear. */
const PAGING_AND_SORT_KEYS = ['pageNumber', 'pageSize', 'sortField', 'sortDirection'];

/**
 * Belong to the session, not the list: runAs (impersonation) and i18nShowNames (translation keys shown
 * beside the text, read by useTranslationLgs). The URL write replaces the whole query string, so these
 * are put back on every write, and kept out of the filter so they are neither sent nor persisted.
 */
const SESSION_KEYS = ['runAs', 'i18nShowNames'];

/** Never a filter value, wherever it turns up. Paging and sorting have their own state. */
const NEVER_FILTER_KEYS = [...SESSION_KEYS, ...PAGING_AND_SORT_KEYS];

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** A page number or size from a URL anyone can edit. Anything that is not a positive integer is not one. */
const parsePositiveInt = (value: string | string[] | undefined, fallback: number): number => {
  if (isNullOrUndefined(value)) return fallback;
  const parsed = Number(first(value));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const getParamsAsArray = (queryString: string) => {
  const params = new URLSearchParams(queryString);
  const result: { [key: string]: string | string[] } = {};

  params.forEach((value, key) => {
    if (!isValidString(value)) return;

    if (result[key]) {
      if (Array.isArray(result[key])) {
        (result[key] as string[]).push(value);
      } else {
        result[key] = [result[key] as string, value];
      }
    } else {
      result[key] = value;
    }
  });

  return result;
};

///https://alpha.ringil.com/cs/customer/expeditions?onlyUnpublished=true&showArchived=true&onlyManual=true&responsiblePersonIds=&pageNumber=1&pageSize=10

export const useFilter = <T, >({ onLoad, filterName, defaultSort, defaultFilter, arrayFields, pagingInUrl = true, filterKeys, defaultPageSize = 10 }: UseFilterProps<T>) => {
  const [filter, setFilter] = useState<T>(defaultFilter);
  const [page, setPage] = useState<FilterPage>({ pageNumber: 1, pageSize: defaultPageSize });
  const [sort, setSort] = useState<FilterSort>(defaultSort);
  const [combined, setCombined] = useState<CombinedValue>();
  // Reload is debounced so rapid successive changes (typing, multi-select picks, range from→to)
  // coalesce into a single fetch. Filter state itself updates immediately, so the UI stays responsive.
  const [debouncedValue] = useDebounce(combined, 600, {});
  const [initialized, setInitialized] = useState(false);
  const [, setSearchParams] = useSearchParams();

  const { loggedUser } = useContext(AuthenticatedUserContext);
  const userId = loggedUser?.id;
  const completeFilterStorageKey = userId
    ? `user_${userId}_${filterName.toLowerCase()}_complete_filter_state`
    : `${filterName.toLowerCase()}_complete_filter_state_anonymous`;

  const [combinedLocalStorage, setCombinedLocalStorage] = useLocalStorage<CombinedValue>(completeFilterStorageKey, combined);

  interface CombinedValue {
    page: FilterPage;
    filter: T;
    sort?: FilterSort;
  }

  /**
   * Stored state can hold keys that were never filters: the hook reads every param on the route into its
   * filter and persists the lot, so old entries carry runAs, a selected tab, carrierReferrer, even toast
   * text. Paging and sorting have their own state, and runAs belongs to the session, so those never come
   * back as filter values.
   */
  const restoreStoredFilter = (stored: T | undefined): T | undefined => {
    if (!stored) return stored;
    const cleaned = Object.fromEntries(
      Object.entries(stored as Record<string, unknown>).filter(([key]) => !NEVER_FILTER_KEYS.includes(key)),
    );
    // Nothing survived, so there is no stored filter to speak of. Say so rather than returning a truthy
    // empty object, which would shadow the caller's defaultFilter.
    return Object.keys(cleaned).length > 0 ? (cleaned as T) : undefined;
  };

  /**
   * Storage holds a shape this hook wrote, until it does not: an older build, a hand-edited entry, a
   * half-written value. Anything that does not look like the state we save is discarded rather than fed
   * to the table, where a filter that is a string used to spread into the URL one character per param.
   */
  const readStoredState = (stored: CombinedValue | undefined): CombinedValue | undefined => {
    if (!isPlainObject(stored)) return undefined;
    if (isNotNullOrUndefined(stored.filter) && !isPlainObject(stored.filter)) return undefined;
    if (isNotNullOrUndefined(stored.sort) && !isPlainObject(stored.sort)) return undefined;
    if (isNotNullOrUndefined(stored.page) && !isPlainObject(stored.page)) return undefined;
    return stored;
  };

  useEffect(() => {
    const allParams = getParamsAsArray(window.location.search);

    // Session keys (runAs, i18nShowNames) say who the request is made as or how text is shown, not what
    // the list is filtered by. They are still written back below, but keeping them out of the filter stops
    // them being sent as filter fields and being persisted into stored state, where they long outlive the
    // session that set them.
    const filteredObject: { [key: string]: string | string[] } = { ...allParams };
    SESSION_KEYS.forEach(key => delete filteredObject[key]);

    // Nothing of this list's in the URL, so the last state it stored is the best thing to show. Session
    // keys are not counted: they are the page's, and a URL carrying only those is still a URL carrying
    // nothing of this list's.
    const hasNoOwnParams = Object.keys(filteredObject).length === 0;

    if (hasNoOwnParams) {
      const stored = readStoredState(combinedLocalStorage);
      // Unusable state would stay unusable on every load, so drop it rather than step around it. The raw
      // string is checked too: useLocalStorage swallows a parse failure and hands back the initial value,
      // so an entry that is not JSON at all never reaches `combinedLocalStorage` to be spotted. Removed
      // outright, since writing `undefined` through the state setter stores the string "undefined".
      if (!stored && isNotNullOrUndefined(window.localStorage.getItem(completeFilterStorageKey))) {
        window.localStorage.removeItem(completeFilterStorageKey);
      }
      if (stored) {
        // An empty result means "nothing stored", not "no filter object": the table renders a loading
        // placeholder for as long as its filter is undefined, so it always gets an object.
        const storedFilter = restoreStoredFilter(stored.filter) ?? defaultFilter ?? ({} as T);
        setPage({ pageNumber: 1, pageSize: defaultPageSize });
        setFilter(storedFilter);
        if (stored.sort) {
          setSort(stored.sort);
        } else if (defaultSort) {
          setSort(defaultSort);
        }
        setCombined({ ...stored, filter: storedFilter });
        setInitialized(true);
        return;
      }
    }

    const page = {
      pageNumber: parsePositiveInt(filteredObject.pageNumber, 1),
      pageSize: parsePositiveInt(filteredObject.pageSize, defaultPageSize),
    } as FilterPage;

    const sort = {
      sortField: filteredObject.sortField?.length > 0 ? first(filteredObject.sortField) : null,
      sortDirection: filteredObject.sortDirection?.length > 0 ? first(filteredObject.sortDirection) : null,
    } as FilterSort;
    // A link can carry a field without a direction. Give it one rather than sorting by null and writing
    // a bare `sortDirection=` back out.
    if (sort.sortField && !sort.sortDirection) {
      sort.sortDirection = defaultSort?.sortDirection ?? 'DESC';
    }

    PAGING_AND_SORT_KEYS.forEach(key => delete filteredObject[key]);

    // Coerce single-value array filters (e.g. ?statusEnums=ASSIGNING) back to arrays so
    // multi-select controls show the selected value.
    (arrayFields ?? []).forEach(field => {
      const value = filteredObject[field];
      if (isNotNullOrUndefined(value) && !Array.isArray(value)) {
        filteredObject[field] = [value as string];
      }
    });

    // The inverse: a repeated param (`?applicationId=a&applicationId=b`, only reachable by editing the
    // URL by hand) parses to an array for a key that holds a single value, and every consumer that
    // treats it as one, `filter.q.trim()` or a chip label, throws. Keep the first value, which is what
    // `first()` above and `URLSearchParams.get` both do. Only keys known to be single-valued are
    // touched, so a list that never declares its fields keeps today's behaviour.
    const singleValueKeys = (filterKeys ?? []).filter(key => !(arrayFields ?? []).includes(key));
    singleValueKeys.forEach(field => {
      const value = filteredObject[field];
      if (Array.isArray(value)) {
        filteredObject[field] = value[0];
      }
    });

    if (sort.sortField === null && defaultSort) {
      setSort(defaultSort);
    } else {
      setSort(sort);
    }

    setPage(page);
    if (Object.keys(filteredObject).length === 0 && isNotNullOrUndefined(defaultFilter)) {
      setFilter(defaultFilter);
    } else {
      setFilter(filteredObject as T);
    }
    // @ts-ignore
    setCombined({ page, filter: filteredObject as T});
    setInitialized(true);
  }, []);

  const applyFilter = (filter: T) => {
    setFilter(filter);
    const newCombined = { ...combined, filter };
    setCombined(newCombined);
    setCombinedLocalStorage(newCombined);
  };

  const names = proxiedPropertiesOf<T>();

  const applyFilterValue = (property: string, value: any) => {
    setFilter(prev => {
      // @ts-ignore
      const newValue = { ...prev, [property]: value };
      return newValue;
    });

    setCombined(prev => {
      // @ts-ignore
      const newValue = {
        ...prev,
        filter: { ...prev?.filter, [property]: value },
      };
      setCombinedLocalStorage(newValue);
      return newValue;
    });
  };

  const applyPaging = (page: FilterPage) => {
    setPage(page);
    setCombined(prev => {
      const newValue = { ...prev, page };
      setCombinedLocalStorage(newValue);
      return newValue;
    });
  };

  const applySorting = (sortField: string, sortDirection: 'ASC' | 'DESC') => {
    setSort({ sortField, sortDirection });
    setCombined(prev => {
      const newValue = { ...prev, sort: { sortField, sortDirection } };
      setCombinedLocalStorage(newValue);
      return newValue;
    });
  };

  useEffect(() => {
    if (initialized && debouncedValue) {
      onLoad(page, filter);
      // The write replaces the whole query string, so the session keys have to be put back or impersonation
      // (runAs) and translation-key display (i18nShowNames) are lost on the first filter change, e.g. when a
      // split-view tab applies its saved filter. Read from the live query string rather than a render-time
      // snapshot, so a key that arrived since this render was scheduled is preserved rather than dropped.
      const liveParams = new URLSearchParams(window.location.search);
      const sessionParams = Object.fromEntries(SESSION_KEYS.filter(key => liveParams.get(key)).map(key => [key, liveParams.get(key)]));
      const value = qs.stringify(
        {
          // @ts-ignore
          ...sessionParams,
          ...filter,
          ...(pagingInUrl ? { pageNumber: page?.pageNumber?.toString(), pageSize: page?.pageSize?.toString() } : {}),
          sortField: debouncedValue?.sort?.sortField,
          sortDirection: debouncedValue?.sort?.sortDirection,
        },
        { arrayFormat: 'repeat' },
      );

      setSearchParams(value, { replace: true });
    }
  }, [debouncedValue, initialized]);

  const onPage = (event: any) => {
    applyPaging({ pageNumber: event.page + 1, pageSize: event.rows });
  };

  const Filter: React.FC<PropsWithChildren<ComponentProps>> = ({ filters, children }) => {
    return <RowGrid>{children}</RowGrid>;
  };

  return {
    Filter,
    applyFilterValue,
    applyPaging,
    filter,
    page,
    onPage,
    applySorting,
    sort,
    applyFilter,
    names,
  };
};

const RowGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  flex-direction: row;
  margin: 0.5rem 0;
  gap: 0.5rem;
`;
